import React from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { normsApis } from "../../../api/Norms";

// Bulk "Apply Norm" upload — rows are {TaskID, NormCode}. Thin wrapper over the
// shared staged-upload modal (upload → validating → preview → committing → done).
// Project-scoped: the current project's id is sent in the body so the TaskID
// (task sequence number) resolves uniquely. Per-row resilient — valid rows apply,
// invalid rows are reported.

// Client-side template so it always matches the parser (two plain columns).
const generateApplyNormTemplate = () => {
  const headers = ["TaskID", "NormCode"];
  const sampleRows = [
    ["T-0001", "B3.1.1"],
    ["T-0002", "B3.1.1"],
    ["T-0003", "B3.1.2"],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [{ wch: 18 }, { wch: 18 }];

  const instructions = [
    ["Apply Norm Upload — Instructions"],
    [""],
    ["TaskID", "Required. The task's number (sequence id) shown in the app, within THIS project. Each row applies a norm to this task."],
    ["NormCode", "Required. The norm code (e.g. B3.1.1). Must be an Active norm with at least one active material line."],
    [""],
    ["Note", "Applying a norm records its materials as the task's requirement. A norm can be applied to a task only ONCE — a row whose task already has that norm is rejected (remove it with a reason first). Invalid rows (unknown task/norm, inactive norm, already applied, duplicate in file) are skipped and listed in the preview; valid rows still apply."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 16 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "ApplyNorm");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "apply-norm-upload-template.xlsx");
};

const rowErrors = (record: any): string[] =>
  Array.isArray(record?.Error)
    ? record.Error
    : record?.Error
    ? [record.Error]
    : [];

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      return record?.status === "Applied" ? (
        <Tag color="blue">Applied</Tag>
      ) : (
        <Tag color="green">Valid</Tag>
      );
    },
  },
  { title: "Task ID", dataIndex: "TaskID", key: "TaskID", width: 120 },
  { title: "Task", dataIndex: "taskName", key: "taskName", width: 220, ellipsis: true },
  { title: "Norm Code", dataIndex: "NormCode", key: "NormCode", width: 120 },
  { title: "Norm Title", dataIndex: "normTitle", key: "normTitle", width: 220, ellipsis: true },
  { title: "Lines Applied", dataIndex: "applied", key: "applied", width: 110 },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 280,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

const ApplyNormUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Apply Norm (Excel)",
      entityLabel: "Apply Norm",
      templateSource: { kind: "client", generate: generateApplyNormTemplate },
      startUpload: (formData) => normsApis.applyNormExcel(formData),
      buildBody: (comments) => ({
        projectId: String(projectId),
        comment: comments ? comments : "N/A",
      }),
      previewColumns,
      rowErrorLabel: (row: any) => (row.TaskID ? `Task ${row.TaskID}` : "Row"),
      copy: {
        uploadIntro: (
          <p>
            Apply norms to many tasks at once. Fill the two-column sheet
            (<b>TaskID</b>, <b>NormCode</b>) for tasks in this project, then upload
            below. You'll see a per-row preview before anything is applied.
          </p>
        ),
        validatingHeading: "Validating your file…",
        validatingSub:
          "Resolving each TaskID and NormCode. This normally takes a few seconds.",
        committingHeading: "Applying norms…",
        committingSub:
          "Applying each norm to its task. Valid rows apply; invalid rows are skipped.",
        commitCta: "Apply Norms",
        doneHeading: "Norms applied",
        doneSub: (report: UploadReportState) => (
          <>
            {report.successCount} row(s) applied
            {report.failureCount ? `, ${report.failureCount} skipped` : ""}. Each
            task's "Materials from Norm" now reflects its norm.
          </>
        ),
        creationFailedNote:
          "Some rows could not be applied — see the errors in the preview.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId],
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default ApplyNormUploadModal;
